import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle, Gear, PaintBrush, WarningCircle } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectClinicProfile,
  setClinicProfile,
} from "../../store/slices/authSlice";
import {
  useUpdateGrowSettingsMutation,
  useSyncGrowBrandingMutation,
} from "../../store/services/growApi";
import { useToast } from "../../contexts/ToastContext";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import GrowSettingsForm from "./GrowSettingsForm";
import { buildGrowSettingsState, serializeGrowSettings } from "./growUi";
import "./GrowIntegration.css";

/**
 * GrowSettingsModal — edit non-secret Grow settings after connect
 * (calls `updateGrowSettings`; the client never writes clinic.growIntegration).
 * "סנכרון מיתוג" pushes logo/colors/button text to Grow via `syncGrowBranding`.
 *
 * Error codes: CK-3865 (save), CK-3866 (branding sync)
 */
export default function GrowSettingsModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const { showSuccess, showError } = useToast();
  const [updateGrowSettings, { isLoading: saving }] = useUpdateGrowSettingsMutation();
  const [syncGrowBranding, { isLoading: syncing }] = useSyncGrowBrandingMutation();

  const [form, setForm] = useState(() => buildGrowSettingsState(clinicProfile, clinicProfile?.growIntegration));
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setForm(buildGrowSettingsState(clinicProfile, clinicProfile?.growIntegration));
      setError("");
    }
  }, [isOpen, clinicProfile]);

  const save = async () => {
    setError("");
    const res = await updateGrowSettings({ clinicId, settings: serializeGrowSettings(form) });
    if (res.error) {
      console.error("[CK-3865] Grow settings save failed", {
        errorCode: "CK-3865",
        errorMessage: res.error.message,
        timestamp: new Date().toISOString(),
      });
      setError(res.error.message);
      return;
    }
    dispatch(setClinicProfile({ ...clinicProfile, growIntegration: res.data?.growIntegration || clinicProfile.growIntegration }));
    showSuccess("הגדרות Grow נשמרו");
    onClose();
  };

  const syncBranding = async () => {
    // Save first so Grow gets the colors currently on screen.
    const saved = await updateGrowSettings({ clinicId, settings: serializeGrowSettings(form) });
    if (saved.error) {
      setError(saved.error.message);
      return;
    }
    dispatch(setClinicProfile({ ...clinicProfile, growIntegration: saved.data?.growIntegration || clinicProfile.growIntegration }));
    const res = await syncGrowBranding({ clinicId });
    if (res.error) {
      console.error("[CK-3866] Grow branding sync failed", {
        errorCode: "CK-3866",
        errorMessage: res.error.message,
        timestamp: new Date().toISOString(),
      });
      showError(res.error.message);
      return;
    }
    if (res.data?.skipped === "no_account_page_code") {
      showError("סנכרון מיתוג דורש קוד עמוד חשבון של Grow (נפרד מקוד דרישת התשלום). ניתן להוסיף אותו דרך התמיכה.");
      return;
    }
    showSuccess(res.data?.syncedLogo ? "המיתוג והלוגו סונכרנו ל-Grow" : "המיתוג סונכרן ל-Grow (ללא לוגו)");
  };

  if (!isOpen) return null;
  const busy = saving || syncing;

  return (
    <div className="grow-overlay" onClick={onClose} role="presentation">
      <div className="grow-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="grow-settings-title">
        <div className="grow-modal-header">
          <div className="grow-modal-title">
            <span className="grow-modal-icon"><Gear size={20} weight="fill" /></span>
            <div>
              <span className="grow-eyebrow">Grow</span>
              <h3 id="grow-settings-title">הגדרות דרישות תשלום</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="grow-modal-body">
          <GrowSettingsForm value={form} onChange={setForm} disabled={busy} />
        </div>

        <div className="grow-modal-footer">
          {error && <span className="grow-status error"><WarningCircle size={14} weight="fill" />{error}</span>}
          <div className="grow-footer-actions">
            <button type="button" className="grow-btn secondary" onClick={syncBranding} disabled={busy} title="שולח לוגו וצבעים ל-Grow">
              {syncing ? <LoaderLoop size="xs" /> : <PaintBrush size={16} />}
              סנכרון מיתוג ל-Grow
            </button>
            <button type="button" className="grow-btn primary" onClick={save} disabled={busy}>
              {saving && !syncing ? <LoaderLoop size="xs" /> : <CheckCircle size={16} weight="bold" />}
              שמירה
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
